import { setGA4UserProperties } from './ga4';
import { identifyPostHogUser } from './posthog';

/**
 * Identifies user across all analytics providers (GA4 + PostHog).
 * Single call replaces separate setGA4UserProperties + identifyPostHogUser calls.
 * @param {Object} options - Same shape as identifyPostHogUser / setGA4UserProperties
 * @param {Object} options.agency - Agency object
 * @param {Object} options.agent - Agent object
 * @param {Object} options.client - Client object
 * @param {string} options.userType - 'agent' or 'client'
 */
export const identifyAnalyticsUser = ({ agency, agent, client, userType }) => {
  setGA4UserProperties({ agency, agent, client, userType });
  identifyPostHogUser({ agency, agent, client, userType });
};

// Reads the global the snippet installs rather than the keyed proxy in ./posthog, which gates on
// REACT_APP_POSTHOG_KEY at module load: the snippet decides whether PostHog exists on a page.
const globalPostHog = () => (typeof window !== 'undefined' ? window.posthog : undefined);

// An absent property must reach the warehouse as a missing property, never as the string 'undefined'.
const withoutUndefined = props => Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined));

/**
 * Captures a product event on the page's PostHog. A no-op wherever the snippet did not load.
 * Every event carries `itinerary_id`, null when the event has none.
 * @param {string} event - The contract's event name, e.g. 'portal:shared'
 * @param {Object} [props] - Event properties; undefined values are dropped
 * @param {Object} [options] - posthog-js capture options, e.g. the transport
 */
export const capture = (event, props, options) => {
  const posthog = globalPostHog();
  if (typeof posthog?.capture !== 'function') return;
  const cleaned = withoutUndefined({ ...props, itinerary_id: props?.itinerary_id ?? null });
  if (options === undefined) {
    posthog.capture(event, cleaned);
    return;
  }
  posthog.capture(event, cleaned, options);
};

// The thresholds BaseVideoPlayer already reports, so every player buckets progress the same way.
const PROGRESS_THRESHOLDS = [10, 25, 50, 75];
const REACHED_30S_SECONDS = 30;

// React hands its handlers a synthetic event and a hand-wired listener may hand over the element
// itself; both resolve to the <video>.
const videoElementOf = eventOrElement => eventOrElement?.currentTarget ?? eventOrElement?.target ?? eventOrElement;

const finiteOrNull = value => (Number.isFinite(value) ? value : null);

// The contract types every *_seconds property as an int.
const wholeSeconds = value => (value === null ? null : Math.round(value));

// Summed from the played TimeRanges, so a seek forward does not count as watching.
const playedSeconds = video => {
  const played = video?.played;
  if (!played || typeof played.length !== 'number') return null;
  let total = 0;
  for (let i = 0; i < played.length; i += 1) total += played.end(i) - played.start(i);
  return total;
};

const percentOf = (position, duration) => (position !== null && duration > 0 ? Math.floor((position / duration) * 100) : null);

// A beacon survives the page being torn down. send_instantly skips the batch queue, which PostHog drains by beacon
// only once its own pagehide handler has run: a visibilitychange exit comes before that, or with no pagehide at all.
const EXIT_CAPTURE_OPTIONS = { transport: 'sendBeacon', send_instantly: true };

/**
 * Builds the native <video> handlers that emit the contract's video:* events for one mount.
 * video:started fires on the first play only, and each progress threshold at most once.
 * video:exited fires when the page hides mid-play and when stop() runs after a play, each time only if
 * played_seconds has advanced since the last video:exited. It carries the $pathname of the first play,
 * because an unmount cleanup can run after the route has changed.
 * @param {Object} options
 * @param {string} options.player - A VIDEO_SURFACE value, sent as video_player
 * @param {string|null} [options.itineraryId]
 * @param {string} [options.videoId]
 * @returns {{ onPlay: Function, onPause: Function, onEnded: Function, onTimeUpdate: Function, stop: Function }}
 */
export const videoTracker = ({ player, itineraryId, videoId }) => {
  let started = false;
  let playing = false;
  let listening = false;
  let playedVideo = null;
  let playPathname;
  // played_seconds of the last video:exited; undefined until one is sent.
  let exitedPlayedSeconds;
  let reached30s = false;
  const crossed = new Set();

  const propsFor = video => {
    const position = finiteOrNull(video?.currentTime);
    // An event can arrive before the timeupdate that crosses 30 s, so the latch is checked here too.
    if (position !== null && position >= REACHED_30S_SECONDS) reached30s = true;
    const duration = finiteOrNull(video?.duration);
    return {
      video_player: player,
      itinerary_id: itineraryId,
      video_id: videoId,
      position_seconds: wholeSeconds(position),
      duration_seconds: wholeSeconds(duration),
      played_seconds: wholeSeconds(playedSeconds(video)),
      percent: percentOf(position, duration),
      reached_30s: reached30s,
    };
  };

  const sendExit = () => {
    const props = propsFor(playedVideo);
    if (exitedPlayedSeconds !== undefined && !(props.played_seconds > exitedPlayedSeconds)) return;
    exitedPlayedSeconds = props.played_seconds;
    capture('video:exited', { ...props, $pathname: playPathname }, EXIT_CAPTURE_OPTIONS);
  };

  const stopListening = () => {
    if (!listening) return;
    listening = false;
    document.removeEventListener('visibilitychange', onPageExit);
    window.removeEventListener('pagehide', onPageExit);
  };

  // A paused or ended video has already reported its seconds, so only a mid-play exit is sent.
  function onPageExit(event) {
    if (event.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
    if (!playing) return;
    sendExit();
  }

  const listen = () => {
    if (listening || typeof window === 'undefined' || typeof document === 'undefined') return;
    listening = true;
    document.addEventListener('visibilitychange', onPageExit);
    window.addEventListener('pagehide', onPageExit);
  };

  return {
    onPlay: eventOrElement => {
      // Taken inside the handler: React clears a synthetic event's currentTarget once it returns.
      playedVideo = videoElementOf(eventOrElement);
      playing = true;
      listen();
      if (started) return;
      started = true;
      playPathname = typeof window !== 'undefined' ? window.location?.pathname : undefined;
      capture('video:started', propsFor(playedVideo));
    },
    onPause: eventOrElement => {
      playing = false;
      capture('video:paused', propsFor(videoElementOf(eventOrElement)));
    },
    onEnded: eventOrElement => {
      playing = false;
      capture('video:completed', propsFor(videoElementOf(eventOrElement)));
    },
    onTimeUpdate: eventOrElement => {
      const video = videoElementOf(eventOrElement);
      const position = finiteOrNull(video?.currentTime);
      if (position !== null && position >= REACHED_30S_SECONDS) reached30s = true;
      const percent = percentOf(position, finiteOrNull(video?.duration));
      if (percent === null) return;
      PROGRESS_THRESHOLDS.forEach(threshold => {
        if (percent < threshold || crossed.has(threshold)) return;
        crossed.add(threshold);
        // On progress, percent is the bucket crossed, which is what the warehouse keys the event on.
        capture('video:progress', { ...propsFor(video), percent: threshold });
      });
    },
    // Call on unmount. After a play it sends video:exited, so in-app navigation keeps the final seconds.
    stop: () => {
      stopListening();
      if (started) sendExit();
    },
  };
};
